import React, { useState } from "react";
import { CheckCircle, WarningCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { C } from "./shared";

const CONF_STYLE = {
  high: { color: C.green, label: "confident" },
  medium: { color: "#F59E0B", label: "check me" },
  low: { color: "#F87171", label: "unsure" },
};

/* Review panel for an Intelligent Financial Import — every extracted figure
   with its confidence, plus David's clarification questions. Low-confidence
   questions must be answered before Apply populates the model. */
export default function ImportReview({ model, onApplied, onDismiss }) {
  const session = model.import_session;
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  if (!session || session.status !== "needs_review") return null;

  const ext = session.extracted || {};
  const questions = session.questions || [];
  const unanswered = questions.filter((q) => answers[q.id] == null || answers[q.id] === "");

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fm/models/${model.fm_id}/import/apply`, { answers });
      toast.success("Import applied — your model is filled in");
      onApplied(data.model);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply the import");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl p-5 mb-6" data-testid="fm-import-review"
      style={{ backgroundColor: C.panel, border: "1px solid rgba(245,158,11,0.4)" }}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-white font-extrabold text-sm">Review your import</div>
          <div className="text-[11px] text-white/45 mt-0.5">
            From {(session.files || []).join(", ")} — confirm the figures below, answer David's questions, then Apply.
          </div>
        </div>
        <button onClick={onDismiss} className="text-white/40 hover:text-white" data-testid="fm-import-dismiss"><X size={15} /></button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
        {(ext.fields || []).slice(0, 9).map((f, i) => {
          const cs = CONF_STYLE[f.confidence] || CONF_STYLE.medium;
          return (
            <div key={i} className="rounded-lg px-3 py-2" style={{ border: "1px solid rgba(255,255,255,0.08)" }}
              data-testid={`fm-import-field-${i}`}>
              <div className="text-[9px] uppercase tracking-widest text-white/35 font-bold">{f.label}</div>
              <div className="text-sm font-extrabold text-white mt-0.5">
                {typeof f.value === "number" ? f.value.toLocaleString("en-KE") : String(f.value ?? "–")}
              </div>
              <div className="text-[9px] font-bold mt-0.5 inline-flex items-center gap-1" style={{ color: cs.color }}>
                {f.confidence === "high" ? <CheckCircle size={10} weight="fill" /> : <WarningCircle size={10} weight="fill" />}
                {cs.label}
              </div>
            </div>
          );
        })}
      </div>

      {questions.length > 0 && (
        <div className="space-y-3 mb-4">
          {questions.map((q) => (
            <div key={q.id} data-testid={`fm-import-q-${q.id}`}>
              <div className="text-[11px] text-white/60 mb-1">{q.question}</div>
              {q.kind === "choice" && (q.options || []).length > 0 ? (
                <select className="w-full rounded-md px-3 py-2 text-sm bg-white/10 text-white border border-white/10"
                  value={answers[q.id] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}>
                  <option value="">Select…</option>
                  {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={q.kind === "number" ? "number" : "text"}
                  className="w-full rounded-md px-3 py-2 text-sm bg-white/10 text-white border border-white/10"
                  placeholder={q.kind === "number" ? "e.g. 5" : "Type your answer"}
                  value={answers[q.id] ?? ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))} />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={apply} disabled={busy || unanswered.length > 0} data-testid="fm-import-apply"
          className="rounded-md px-5 py-2.5 text-sm font-bold disabled:opacity-40"
          style={{ backgroundColor: C.orange, color: "#1A1409" }}>
          {busy ? "Applying…" : "Apply to my model"}
        </button>
        {unanswered.length > 0 && (
          <span className="text-[11px]" style={{ color: C.orange }} data-testid="fm-import-unanswered">
            {unanswered.length} question{unanswered.length > 1 ? "s" : ""} left — David can help in chat
          </span>
        )}
      </div>
    </div>
  );
}
